import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Document, Page, pdfjs } from "react-pdf";
import { DocumentBlockLayer } from "./lib/DocumentBlockLayer";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AllAvailableBlocks from "./lib/AllAvailableBlocks";
import Constants from "const/Constants";
import EditDocumentUtils from "utils/EditDocument";
import IntegrationServices from "const/IntegrationServices";
import MainApiActions from "actions/MainApiActions";
import Preloader from "nlib/common/Preloader";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Texts from "utils/Texts";
import classNames from "classnames";
import useDocumentContext from "hooks/useDocumentContext";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const SCROLL_WIDTH = 24;

const FLIP_DIMENSIONS_ANGLE = [
  Constants.QUARTER_CIRCLE_DEGREES,
  Constants.THREE_QUARTERS_CIRCLE_DEGREES
];

const DocumentRenderer = (props) => {
  const {
    containerWidth,
    firstPageSided,
    pageWidthScale,
    pageFitScale,
    pagesData,
    scrollContainerRef,
    documentScale,
    numPages,
    setPagesData,
    setNumPages,
    setDocumentScale
  } = props;

  const componentMounted = useRef(false);

  const dispatch = useDispatch();

  const prevDocumentScale = useRef();

  const { attachment, ocrMeta, localState: { activeField } } = useDocumentContext();

  const [fileUrl, setFileUrl] = useState(null);

  const [dragStart, setDragStart] = useState(null);

  const [documentLoaded, setDocumentLoaded] = useState(false);

  const { uiTexts } = useSelector(getTextsData);

  const { extraData: { integrationService } } = useSelector(getSelectedBusinessData);

  const { logo: serviceLogo, label: serviceName } = IntegrationServices.getByValue(integrationService);

  const wordBlocks = useMemo(() => {
    if (!ocrMeta.wordBlocks) return [];

    return ocrMeta.wordBlocks.map((block) => {
      const { boundingBox: { page: index } } = block;

      const pageData = pagesData[index];

      if (!pageData) return null;

      return {
        ...block,
        boundingBox: EditDocumentUtils.rotateBoundingBox({
          boundingBox: block.boundingBox,
          angle: EditDocumentUtils.normalizeAngle(ocrMeta.pages?.[index]?.angle || 0),
          width: pageData.width || 0,
          height: pageData.height || 0
        })
      };
    }).filter(Boolean);
  }, [ocrMeta.pages, ocrMeta.wordBlocks, pagesData]);

  const contentWidth = (firstPageSided ? pagesData[0]?.height : pagesData[0]?.width) || 0;

  const pageHeights = useMemo(() => {
    return pagesData.map((data) => {
      return (data?.swapDimensionsForLayout ? data?.width : data?.height) || 0;
    });
  }, [pagesData]);

  const pageOffsets = useMemo(() => {
    return pagesData.map(({ viewportOffset } = {}) => viewportOffset || { left: 0, top: 0 });
  }, [pagesData]);

  const pageLayoutData = useMemo(() => {
    return {
      docPageHeights: pageHeights,
      docPageOffsets: pageOffsets,
      docRenderWidth: contentWidth
    };
  }, [contentWidth, pageHeights, pageOffsets]);

  const fetchFileUrl = useCallback(async() => {
    const result = await dispatch(MainApiActions.fetchAttachmentUrl(attachment));

    if (!componentMounted.current) return;
    setFileUrl(result);
  }, [attachment, dispatch]);

  const handleDocumentLoadSuccess = useCallback((data) => {
    setNumPages(data.numPages);
    setDocumentLoaded(true);
  }, [setNumPages]);

  const handlePageRenderSuccess = useCallback((data) => {
    const { rotate: initialRotate, height, width, originalHeight, originalWidth, view, _pageIndex: index } = data;

    const intrinsicRotate = initialRotate || 0;

    const rotate = intrinsicRotate || EditDocumentUtils.normalizeAngle(ocrMeta.pages?.[index]?.angle || 0);

    const swapDimensionsForLayout = !intrinsicRotate && FLIP_DIMENSIONS_ANGLE.includes(rotate);

    const scale = originalWidth ? (width / originalWidth) : 1;

    setPagesData((prev) => {
      const newData = [...prev];

      newData[index] = {
        rotate,
        height,
        width,
        originalHeight,
        originalWidth,
        swapDimensionsForLayout,
        viewportOffset: EditDocumentUtils.getPageViewportOffset({ view, rotate, scale })
      };

      return newData;
    });
  }, [ocrMeta.pages, setPagesData]);

  const handleMouseDown = useCallback(({ clientX, clientY }) => {
    if (activeField) return;

    const { scrollTop, scrollLeft } = scrollContainerRef.current;

    setDragStart({ clientX, clientY, scrollTop, scrollLeft });
  }, [activeField, scrollContainerRef]);

  const handleMouseUp = useCallback(() => {
    setDragStart(null);
  }, []);

  const handleMouseMove = useCallback((event) => {
    if (dragStart) {
      const { clientX, clientY, scrollTop, scrollLeft } = dragStart;

      scrollContainerRef.current.scrollTop = scrollTop + clientY - event.clientY;
      scrollContainerRef.current.scrollLeft = scrollLeft + clientX - event.clientX;
    }
  }, [dragStart, scrollContainerRef]);

  useLayoutEffect(() => {
    componentMounted.current = true;
    if (!Object.keys(attachment).length) {
      setNumPages(-1);
    } else if (!fileUrl) {
      fetchFileUrl();
    }

    return () => {
      componentMounted.current = false;
    };
  }, [fileUrl, dispatch, fetchFileUrl, attachment, setNumPages]);
  useEffect(() => {
    if (!isNaN(documentScale) && prevDocumentScale.current && prevDocumentScale.current !== documentScale) {
      const { scrollTop, scrollLeft } = scrollContainerRef.current;

      scrollContainerRef.current.scrollTop = scrollTop * documentScale / prevDocumentScale.current;
      scrollContainerRef.current.scrollLeft = scrollLeft * documentScale / prevDocumentScale.current;
      prevDocumentScale.current = documentScale;
    }
  }, [documentScale, scrollContainerRef]);
  useEffect(() => {
    if (documentScale === "pageFit") setDocumentScale(pageFitScale);
    if (documentScale === "pageWidth") setDocumentScale(pageWidthScale);
  }, [documentScale, pageFitScale, pageWidthScale, setDocumentScale]);
  useEffect(() => {
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseUp]);

  return (
    <div
      className={classNames(Css.documentRenderer, !activeField && Css.grabbable)}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}>
      {numPages === -1 && (
        <div className={Css.dummyFile}>
          <img src={serviceLogo} />
          <Icons.File weight="thin" className={Css.dummyIcon} />
          <div className={Css.dummyText}>
            {Texts.replaceVars(uiTexts.documentImportedFrom, { serviceName })}
          </div>
        </div>
      )}
      {!!fileUrl && (
        <div className={Css.document}>
          <Document
            loading={null}
            file={fileUrl}
            onLoadSuccess={handleDocumentLoadSuccess}>
            {[...(new Array(numPages || 0))].map((item, pageIndex) => {
              return documentLoaded && (
                <Page
                  key={String(pageIndex + 1)}
                  rotate={pagesData[pageIndex]?.rotate || 0}
                  scale={isNaN(documentScale) ? undefined : documentScale}
                  width={documentScale ? undefined : (containerWidth - SCROLL_WIDTH)}
                  loading={<Preloader absolute />}
                  pageIndex={pageIndex}
                  onRenderSuccess={handlePageRenderSuccess}
                  renderAnnotationLayer={false}
                  renderTextLayer={false} />
              );
            })}
          </Document>
          <AllAvailableBlocks
            wordBlocks={wordBlocks}
            pageLayoutData={pageLayoutData} />
          <DocumentBlockLayer
            wordBlocks={wordBlocks}
            scrollContainerRef={scrollContainerRef}
            pageLayoutData={pageLayoutData}
            pagesData={ocrMeta.pages}
            width={contentWidth} />
        </div>
      )}
    </div>
  );
};

export default React.memo(DocumentRenderer);
